"use client";

import { useState, useCallback } from "react";
import FileUploader from "./FileUploader";
import ProcessingOverlay from "./ProcessingOverlay";
import DownloadResult from "./DownloadResult";
import SignupGateModal from "./SignupGateModal";
import UsageLimitBanner from "./UsageLimitBanner";
import { useToolFetch } from "@/hooks/useToolFetch";

const QUALITY_OPTIONS = [
  { value: "high", label: "High Quality", desc: "Minimal compression, best visual quality" },
  { value: "medium", label: "Balanced", desc: "Good balance of size and quality" },
  { value: "low", label: "Maximum", desc: "Smallest file size, may reduce image quality" },
] as const;

export default function CompressPdfTool() {
  const [file, setFile] = useState<File | null>(null);
  const [quality, setQuality] = useState<string>("medium");
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<{ url: string; size: number } | null>(null);
  const [error, setError] = useState("");
  const { toolFetch, quotaError } = useToolFetch();

  const handleFilesSelected = useCallback((files: File[]) => {
    setFile(files[0] || null);
    setResult(null);
    setError("");
  }, []);

  const compress = useCallback(async () => {
    if (!file) return;
    setProcessing(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("quality", quality);

      const res = await toolFetch("/api/tools/compress", { method: "POST", body: formData });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to compress PDF");
      }

      const blob = await res.blob();
      setResult({ url: URL.createObjectURL(blob), size: blob.size });
    } catch (e: any) {
      setError(e.message || "Failed to compress PDF");
    } finally {
      setProcessing(false);
    }
  }, [file, quality]);

  const reset = useCallback(() => {
    setFile(null);
    setResult(null);
    setError("");
  }, []);

  if (result) {
    const savedPct = file ? Math.round((1 - result.size / file.size) * 100) : 0;
    return (
      <div>
        {savedPct > 0 && (
          <div className="mb-4 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-center">
            <p className="text-sm font-semibold text-emerald-700">
              Reduced by {savedPct}% — from {(file!.size / 1024 / 1024).toFixed(1)}MB to {(result.size / 1024 / 1024).toFixed(1)}MB
            </p>
          </div>
        )}
        <DownloadResult fileName="compressed.pdf" fileSize={result.size} downloadUrl={result.url} onReset={reset} />
      </div>
    );
  }

  if (quotaError?.gate === "signup") return <SignupGateModal quotaError={quotaError} />;

  return (
    <div className="relative space-y-6">
      {processing && <ProcessingOverlay message="Compressing your PDF..." />}
      {quotaError?.gate === "limit" && <UsageLimitBanner quotaError={quotaError} />}

      <FileUploader accept=".pdf" onFilesSelected={handleFilesSelected} label="Drop your PDF here" description="Reduce file size without losing quality" />

      {file && (
        <>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Compression level</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {QUALITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setQuality(opt.value)}
                  className={`rounded-lg border-2 px-4 py-3 text-left transition-all ${
                    quality === opt.value
                      ? "border-[var(--color-gold)] bg-[var(--color-gold-muted)]/30"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <p className="text-sm font-semibold text-slate-800">{opt.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="text-sm text-slate-500">
            Original size: <strong>{(file.size / 1024 / 1024).toFixed(2)} MB</strong>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button onClick={compress} disabled={processing} className="btn-primary w-full py-3">
            Compress PDF
          </button>
        </>
      )}
    </div>
  );
}
